import { Entity } from '@backstage/catalog-model';
import {
  EntityFilter,
  DefaultEntityFilters,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { default as React } from 'react';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

const MICROAPP_ANNOTATION_KEY = 'skystage.wellsky.com/type';
const MICROAPP_ANNOTATION_VALUE = 'microapp';

// Define the filter that will check for the existence of the annotation
export class MicroappTypeFilter implements EntityFilter {
  constructor(readonly showWithAnnotation: boolean) {}

  filterEntity(entity: Entity): boolean {
    const hasAnnotation =
      entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
      MICROAPP_ANNOTATION_VALUE;
    return this.showWithAnnotation ? hasAnnotation : !hasAnnotation;
  }
}

// Extend the default filters with our custom filter
export type CustomFilters = DefaultEntityFilters & {
  microappType?: MicroappTypeFilter;
};

// Create a component to control the filter
export const MicroappTypePicker = ({ initialShowMicroapps = true }) => {
  const {
    filters: { microappType },
    updateFilters,
  } = useEntityList<CustomFilters>();

  // Use our initialValue prop or the existing filter value
  const showWithAnnotation =
    microappType?.showWithAnnotation ?? initialShowMicroapps;

  // Set initial filter value on component mount
  React.useEffect(() => {
    updateFilters({
      microappType: new MicroappTypeFilter(initialShowMicroapps),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle between showing entities with/without the annotation
  function onChange() {
    updateFilters({
      microappType: new MicroappTypeFilter(!showWithAnnotation),
    });
  }

  return (
    <FormControl component="fieldset">
      <Typography variant="button">Microapp Type</Typography>
      <FormControlLabel
        control={<Switch checked={showWithAnnotation} onChange={onChange} />}
        label={showWithAnnotation ? 'Show Microapps' : 'Hide Microapps'}
      />
    </FormControl>
  );
};

// Invisible filters that only apply the filter without showing UI controls
export const MicroappsOnlyFilter = () => {
  const { updateFilters } = useEntityList<CustomFilters>();

  // Set filter to show only microapps on component mount
  React.useEffect(() => {
    updateFilters({
      microappType: new MicroappTypeFilter(true),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return null so nothing is rendered
  return null;
};

export const RegularProjectsFilter = () => {
  const { updateFilters } = useEntityList<CustomFilters>();

  // Set filter to hide microapps on component mount
  React.useEffect(() => {
    updateFilters({
      microappType: new MicroappTypeFilter(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return null so nothing is rendered
  return null;
};
